import { useTheme } from '../lib/theme-provider';
import { ThemedCard } from './ThemedCard';
import { ThemedButton } from './ThemedButton';
import { User, X, Eye, EyeOff, Users } from 'lucide-react';

interface Player {
  id: string;
  seatNumber: number;
  nickname: string;
  role: string;
  team: 'good' | 'evil';
  isVisible: boolean;
}

interface IdentityRevealModalProps {
  isOpen: boolean;
  currentPlayer: {
    role: string;
    team: 'good' | 'evil';
    visiblePlayers: Player[];
  };
  onClose: () => void;
}

export default function IdentityRevealModal({
  isOpen,
  currentPlayer,
  onClose,
}: IdentityRevealModalProps) {
  const { theme } = useTheme();

  if (!isOpen) return null;

  const getRoleDescription = (role: string) => {
    const descriptions: Record<string, string> = {
      '梅林': '你知道所有邪恶阵营玩家的身份，但不能暴露自己',
      '派西维尔': '你可以看到梅林和莫甘娜，但需要分辨谁是真梅林',
      '忠臣': '你是正义阵营的普通成员，帮助梅林隐藏身份',
      '刺客': '游戏结束时可以刺杀一名玩家，如果刺杀梅林则邪恶阵营获胜',
      '莫甘娜': '你看起来和梅林一样，可以迷惑派西维尔',
      '莫德雷德': '梅林看不到你的身份',
      '奥伯伦': '其他邪恶阵营玩家看不到你的身份',
    };
    return descriptions[role] || '暂无描述';
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
    >
      <ThemedCard className="w-full max-w-md p-6">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <div
            className="flex items-center gap-2"
            style={{
              color:
                currentPlayer.team === 'good'
                  ? theme.colors['accent-green']
                  : theme.colors['accent-red'],
            }}
          >
            <User size={24} />
            <h2
              className="text-xl font-bold"
              style={{ color: theme.colors['text-primary'] }}
            >
              你的身份
            </h2>
          </div>
        </div>

        {/* 身份卡片 */}
        <div
          className="mb-6 p-6 rounded-xl text-center"
          style={{
            backgroundColor:
              currentPlayer.team === 'good'
                ? `${theme.colors['accent-green']}15`
                : `${theme.colors['accent-red']}15`,
            border: `2px solid ${
              currentPlayer.team === 'good'
                ? theme.colors['accent-green']
                : theme.colors['accent-red']
            }`,
          }}
        >
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-bold"
            style={{
              backgroundColor:
                currentPlayer.team === 'good'
                  ? theme.colors['accent-green']
                  : theme.colors['accent-red'],
              color: theme.colors['bg-primary'],
            }}
          >
            {currentPlayer.role[0]}
          </div>
          <h3
            className="text-2xl font-bold mb-2"
            style={{
              color:
                currentPlayer.team === 'good'
                  ? theme.colors['accent-green']
                  : theme.colors['accent-red'],
            }}
          >
            {currentPlayer.role}
          </h3>
          <p
            className="text-sm mb-2"
            style={{ color: theme.colors['text-secondary'] }}
          >
            {currentPlayer.team === 'good' ? '正义阵营' : '邪恶阵营'}
          </p>
          <p
            className="text-xs"
            style={{ color: theme.colors['text-muted'] }}
          >
            {getRoleDescription(currentPlayer.role)}
          </p>
        </div>

        {/* 可见玩家 */}
        {currentPlayer.visiblePlayers.length > 0 && (
          <div className="mb-6">
            <h3
              className="text-sm font-medium mb-3 flex items-center gap-2"
              style={{ color: theme.colors['text-secondary'] }}
            >
              <Eye size={16} />
              你可以看到的玩家
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {currentPlayer.visiblePlayers.map((player) => (
                <div
                  key={player.id}
                  className="flex flex-col items-center p-2 rounded-lg"
                  style={{
                    backgroundColor: theme.colors['bg-primary'],
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold mb-1"
                    style={{
                      backgroundColor:
                        player.team === 'good'
                          ? theme.colors['accent-green']
                          : theme.colors['accent-red'],
                      color: theme.colors['bg-primary'],
                    }}
                  >
                    {player.nickname[0]}
                  </div>
                  <span
                    className="text-xs truncate w-full text-center"
                    style={{ color: theme.colors['text-primary'] }}
                  >
                    {player.nickname}
                  </span>
                  <span
                    className="text-xs"
                    style={{ color: theme.colors['text-muted'] }}
                  >
                    #{player.seatNumber}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 不可见提示 */}
        {currentPlayer.visiblePlayers.length === 0 && (
          <div
            className="mb-6 p-4 rounded-lg text-center text-sm"
            style={{
              backgroundColor: theme.colors['bg-primary'],
              color: theme.colors['text-muted'],
            }}
          >
            <EyeOff size={20} className="mx-auto mb-2" />
            本局游戏中你看不到其他玩家的身份
          </div>
        )}

        {/* 操作按钮 */}
        <ThemedButton onClick={onClose} className="w-full">
          <X size={18} className="mr-1" />
          关闭
        </ThemedButton>
      </ThemedCard>
    </div>
  );
}
